import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { useAppConfig } from "@/lib/appConfig";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  // Desktop app = private install: only email + password. Wait for the config
  // before showing sign-up options so they never flash on the desktop.
  const { signupEnabled, ready, appVersion, desktop } = useAppConfig();
  const { ownerEntry } = useAuth();
  // Desktop: the owner has no password. Open straight in, unless someone just
  // signed out (so staff can sign in) — then offer a one-click button.
  const [owner, setOwner] = useState(null); // { name } when password-free owner exists
  const [entering, setEntering] = useState(false);
  const goNext = () => nav(decodeURIComponent(params.get("next") || "/dashboard"), { replace: true });
  const enterAsOwner = async () => {
    setEntering(true);
    try { await ownerEntry(); goNext(); }
    catch (err) { setError(extractErrorMessage(err)); setEntering(false); }
  };
  useEffect(() => {
    if (!ready || !desktop) return;
    api.get("/auth/owner-entry").then((r) => {
      if (!r.data?.available) return;
      setOwner({ name: r.data.name });
      let signedOut = false;
      try { signedOut = localStorage.getItem("ubos.signedOut") === "1"; } catch { /* ignore */ }
      if (!signedOut) enterAsOwner();
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, desktop]);
  const showSignup = ready && signupEnabled;

  useEffect(() => {
    api.get("/auth/google/status").then((r) => setGoogleEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({ email, password });
      const next = params.get("next") || "/dashboard";
      nav(decodeURIComponent(next), { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const startGoogle = async () => {
    try {
      const redirect_uri = `${window.location.origin}/auth/google/callback`;
      const res = await api.get("/auth/google/login", { params: { redirect_uri } });
      window.location.assign(res.data.url);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (entering) {
    return (
      <div data-testid="owner-entering" className="text-sm text-muted-foreground">Opening your workspace…</div>
    );
  }

  return (
    <div data-testid="login-page">
      <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
      <p className="text-sm text-muted-foreground mt-1.5">
        Welcome back. Continue where you left off.
      </p>

      {owner && (
        <div className="mt-8">
          <Button type="button" className="w-full" onClick={enterAsOwner} data-testid="owner-entry-btn">
            Continue as {owner.name || "Owner"}
          </Button>
          <div className="my-6 relative">
            <div className="h-px bg-border" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-[11px] font-mono uppercase text-muted-foreground">
              or sign in as staff
            </span>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className={owner ? "space-y-4" : "mt-8 space-y-4"}>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            data-testid="input-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              className="text-xs text-primary hover:underline"
              data-testid="link-forgot-password"
            >
              Forgot?
            </Link>
          </div>
          <Input
            id="password"
            data-testid="input-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive" data-testid="login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          className="w-full"
          disabled={busy}
          data-testid="submit-login"
        >
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      {showSignup && (
        <>
          <div className="my-6 relative">
            <div className="h-px bg-border" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-[11px] font-mono uppercase text-muted-foreground">
              or
            </span>
          </div>

          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="block w-full"
                  data-testid="google-signin-wrap"
                  tabIndex={!googleEnabled ? 0 : -1}
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full pointer-events-auto"
                    onClick={startGoogle}
                    disabled={!googleEnabled}
                    data-testid="google-signin-btn"
                  >
                    <GoogleIcon /> Continue with Google
                  </Button>
                </span>
              </TooltipTrigger>
              {!googleEnabled && (
                <TooltipContent
                  side="bottom"
                  data-testid="google-signin-tooltip"
                >
                  Google Sign-In not configured for this environment
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="text-primary font-medium hover:underline"
              data-testid="link-register"
            >
              Create one
            </Link>
          </p>
        </>
      )}
      {appVersion && (
        <p className="mt-10 text-center text-[11px] font-mono text-muted-foreground/70" data-testid="app-version">
          UBOS v{appVersion}
        </p>
      )}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.3-1.7 3.9-5.5 3.9-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 9.6-4.8 9.6-9.6 0-.6-.1-1.2-.2-1.8H12z" />
    </svg>
  );
}
